// =========================================
// BY ZY — Live Content Source (R2)
// Fetches portfolio + overrides from Cloudflare R2.
// Fallback: localStorage cache → bundled local JSON.
//
// Cache invalidation: uses meta.updatedAt from the
// published JSON to detect freshness. If the R2 copy
// has a newer updatedAt than the cached one, the cache
// is refreshed. No hard TTL — always attempt R2 first.
// =========================================

import localPortfolio from '../data/portfolio.local.json';
import { normalizeMediaItem } from './overrides';

const R2_BASE = (process.env.REACT_APP_R2_PUBLIC_BASE_URL || '').replace(/\/+$/, '');
const CACHE_KEY = 'byzy_live_content';
const VERSION_KEY = 'byzy_content_version';

/**
 * Normalize raw JSON into the shape the app expects.
 * Same logic as content.js to avoid coupling.
 */
function normalize(json) {
  if (!json || !json.categories) return null;
  const cats = json.categories;
  const result = {};

  for (const [key, cat] of Object.entries(cats)) {
    result[key] = {
      title: cat.title || '',
      subtitle: cat.subtitle || '',
      clients: (cat.clients || []).map((c) => ({
        id: c.id || c.slug || c.name.toLowerCase().replace(/\s+/g, '-'),
        name: c.name,
        media: (c.media || []).map(normalizeMediaItem),
      })),
    };
  }

  return result;
}

/** Read cached content from localStorage. Returns null if missing. */
function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.portfolio) return null;
    return parsed; // { portfolio, overrides, ts, updatedAt }
  } catch {
    return null;
  }
}

/** Write content to localStorage cache (includes updatedAt for freshness). */
function writeCache(portfolio, overrides, updatedAt) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      portfolio,
      overrides,
      ts: Date.now(),
      updatedAt: updatedAt || '',
    }));
  } catch { /* full or unavailable */ }
}

/**
 * Load live content from R2 (portfolio + overrides).
 *
 * Priority:
 * 1. Fetch from R2 (`data/portfolio.json`)
 *    - Compare meta.updatedAt with cached copy
 *    - If unchanged, reuse cache (avoid re-parse overhead)
 *    - If changed or no cache, store fresh data
 * 2. Use localStorage cache if fetch fails (no TTL — always valid)
 * 3. Use bundled local JSON as final fallback
 *
 * Returns: { portfolio: { video, web }, overrides: object|null }
 */
export async function loadLiveContent() {
  const cached = readCache();

  // 1. Try R2
  if (R2_BASE) {
    // Use admin-published version for cache busting if available.
    // For new visitors without VERSION_KEY, add a 5-min time bucket
    // so CDN caches expire reasonably fast after a publish.
    const version = localStorage.getItem(VERSION_KEY) || '';
    const timeBucket = Math.floor(Date.now() / (2 * 60 * 1000));
    const cacheBuster = version ? `v=${version}` : `t=${timeBucket}`;
    const url = `${R2_BASE}/data/portfolio.json?${cacheBuster}`;

    try {
      const res = await fetch(url, {
        cache: 'no-cache',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();

      // Extract updatedAt from the published JSON
      const updatedAt = json.meta?.updatedAt || json.publishedAt || '';

      // Smart cache: if meta.updatedAt matches cached copy, skip re-normalize
      if (updatedAt && cached && cached.updatedAt === updatedAt && cached.portfolio) {
        return { portfolio: cached.portfolio, overrides: cached.overrides };
      }

      const portfolio = normalize(json);
      const overrides = json._overrides || null;

      if (portfolio) {
        writeCache(portfolio, overrides, updatedAt);
        return { portfolio, overrides };
      }
    } catch (err) {
      console.warn('[BY ZY] R2 content fetch failed, using fallback:', err.message);
    }
  }

  // 2. Cache fallback (no hard TTL — any cached data is better than nothing)
  if (cached && cached.portfolio) {
    return { portfolio: cached.portfolio, overrides: cached.overrides };
  }

  // 3. Bundled local fallback
  return { portfolio: normalize(localPortfolio), overrides: null };
}
